import CloseIcon from '@assets/icons/close-circle-x-large.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { Pressable, View } from 'react-native';
import { TabCardDetails } from '@/components/payments/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useTabCardImage } from '@/hooks/payments/use-tab-card-image';

const CLOSE_ICON_SIZE = 24;

interface TabCardEnrollmentBannerLinkProps {
  onDismiss?: () => void;
  onPress: () => void;
  tabCardDetails: TabCardDetails;
}

export const TabCardEnrollmentBannerLink: React.FC<
  TabCardEnrollmentBannerLinkProps
> = ({ onDismiss, onPress, tabCardDetails }) => {
  const { distributorName, imageUrl } = tabCardDetails;
  const { source, onImageLoadError } = useTabCardImage(imageUrl);
  const [styles, theme] = useStyles();
  const hitSlop = theme.sk.spacing.spaceMedium;

  const title = i18n.t('tab_card_banner.title');

  const dismissButton = Boolean(onDismiss) ? (
    <Pressable
      accessibilityLabel={i18n.t('dismiss')}
      accessibilityRole="button"
      hitSlop={{ bottom: hitSlop, left: hitSlop, right: hitSlop, top: hitSlop }}
      onPress={onDismiss}
      style={({ pressed }) => [styles.dismissButton, pressed && styles.pressed]}
    >
      <CloseIcon height={CLOSE_ICON_SIZE} width={CLOSE_ICON_SIZE} />
    </Pressable>
  ) : null;

  return (
    <View
      accessibilityLabel={title}
      accessibilityRole="summary"
      accessible
      style={styles.container}
    >
      <View style={styles.content}>
        <View style={styles.textContainer}>
          <Typography color={theme.sk.color.text.primary} variant="label-md">
            {title}
          </Typography>
          <Typography
            color={theme.sk.color.text.secondary}
            variant="paragraph-sm"
          >
            {i18n.t('tab_card_banner.subtitle', { distributorName })}
          </Typography>
          <Pressable
            onPress={onPress}
            style={({ pressed }) => [
              styles.learnMore,
              pressed && styles.pressed,
            ]}
          >
            {({ hovered }) => (
              <Typography
                color={theme.sk.color.text.primary}
                textDecorationLine={hovered ? 'none' : 'underline'}
                variant="label-sm"
              >
                {i18n.t('learn_more')}
              </Typography>
            )}
          </Pressable>
        </View>
        <CachedImage
          accessibilityIgnoresInvertColors
          alt="payment card"
          onError={onImageLoadError}
          source={source}
          style={styles.cardImage}
        />
      </View>
      {dismissButton}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  cardImage: {
    height: 75,
    width: 119,
  },
  container: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    padding: theme.sk.spacing.spaceMedium,
  },
  content: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  dismissButton: {
    position: 'absolute',
    right: theme.sk.spacing.spaceSmall,
    top: theme.sk.spacing.spaceSmall,
  },
  learnMore: {
    alignSelf: 'flex-start',
  },
  pressed: {
    opacity: 0.5,
  },
  textContainer: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
